import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { isValidShortCode } from '../../utils/shortCodeUtils';
import { apiUrl } from '../../utils/apiBase';
import EscaloneLoading from '../EscaloneLoading';

interface CardRedirectProps {
  onCardResolved?: (flowId: string, cardId: string) => void;
}

/**
 * Componente que resolve um shortCode para o card real e redireciona
 * Rota: /c/:shortCode
 * 
 * Se o usuário está logado, redireciona para o flow com o card aberto.
 * Se não está logado, redireciona para login com retorno programado.
 */
const CardRedirect = ({ onCardResolved }: CardRedirectProps) => {
  const { shortCode } = useParams<{ shortCode: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const resolveCard = async () => {
      if (!shortCode) {
        setError('Código não fornecido');
        setLoading(false);
        return;
      }

      // Validar formato do shortCode
      if (!isValidShortCode(shortCode)) {
        setError('Código inválido');
        setLoading(false);
        return;
      }

      try {
        console.log('🔍 Resolvendo shortCode:', shortCode);
        
        // Buscar card pelo shortCode na API
        // Nota: apiUrl já inclui /api, então passamos apenas o caminho relativo
        const response = await fetch(apiUrl(`/cards/by-shortcode/${shortCode}`));
        const data = await response.json();

        if (!response.ok || !data.success) {
          console.error('❌ Card não encontrado:', data.error);
          setError('Card não encontrado');
          setLoading(false);
          return;
        }

        const { flowId, cardId, titulo } = data;
        console.log('✅ Card resolvido:', { flowId, cardId, titulo });

        // Verificar se há sessão
        const sessionStr = localStorage.getItem('session');
        const session = sessionStr ? JSON.parse(sessionStr) : null;

        if (session?.usuario) {
          // Usuário logado - redirecionar para o flow com o card aberto
          // Salvar o cardId no sessionStorage para abrir automaticamente
          sessionStorage.setItem('pendingCardOpen', JSON.stringify({ flowId, cardId }));
          
          // Notificar o App se callback fornecido
          if (onCardResolved) {
            onCardResolved(flowId, cardId);
          }
          
          // Redirecionar para a raiz com indicação de que deve abrir o card
          navigate(`/`, { replace: true });
        } else {
          // Usuário não logado - salvar para depois e ir para login
          sessionStorage.setItem('redirectAfterLogin', JSON.stringify({
            type: 'card',
            flowId,
            cardId,
            shortCode
          }));
          navigate('/login', { replace: true });
        }
      } catch (err) {
        console.error('❌ Erro ao resolver shortCode:', err);
        setError('Erro ao buscar card');
        setLoading(false);
      }
    };

    resolveCard();
  }, [shortCode, navigate, onCardResolved]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-white to-purple-50">
        <div className="text-center">
          <EscaloneLoading />
          <p className="mt-4 text-gray-600">Abrindo card...</p>
          <p className="text-sm text-gray-400 font-mono">#{shortCode}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-white to-purple-50">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Card não encontrado</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-400 font-mono mb-6">Código: #{shortCode}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Ir para Home
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default CardRedirect;
